import React, { FormEvent, useEffect, useState } from "react";
import { Card } from "primereact/card";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { SelectButton } from "primereact/selectbutton";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import { TransactionsPeriodSelector } from "./TransactionsPeriodSelector";
import {
  Transaction,
  TransactionOrder,
  TransactionSearch,
  TransactionSearchKind,
} from "../../types/transactions";
import { ChargeType, DateRange } from "./hooks/useTransactionsExplorer";
import { Tag } from "primereact/tag";
import { formatDate } from "../../utils/formats";
import {
  formatPaymentsSummary,
  otherSearchKind,
  SEARCH_KIND_LABEL,
} from "./utils/transactionSearch";

const CHARGE_TYPE_OPTIONS: { label: string; value: ChargeType }[] = [
  { label: "All", value: "all" },
  { label: "Paid", value: "paid" },
  { label: "Free", value: "free" },
];

const SKELETON_ROW_COUNT = 10;

// Placeholder rows shown while a page loads (CLR-278): the table keeps its shape and
// every cell renders a Skeleton instead of an empty body with a spinner.
const SKELETON_ROWS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => ({ cdrId: `skeleton-${i}` }) as Transaction
);

// Keep the charge-type segmented control on a single horizontal row: PrimeReact's
// SelectButton stretches its buttons when its parent is a flex column / narrow, so
// pin the row direction and stop the labels wrapping.
const HEADER_STYLE = `
  .charge-type-select.p-selectbutton {
    display: inline-flex;
    flex-wrap: nowrap;
  }
  .charge-type-select.p-selectbutton .p-button {
    flex: 0 0 auto;
    white-space: nowrap;
  }
`;

interface TransactionsTableHeaderProps {
  appliedRange: DateRange;
  applyPeriod: (period: DateRange) => void;
  clearPeriod: () => void;
  chargeType: ChargeType;
  setChargeType: (value: ChargeType) => void;
  search: TransactionSearch;
  applySearch: (value: string, kind?: TransactionSearchKind) => void;
  summary: string;
}

const TransactionsTableHeader = ({
  appliedRange,
  applyPeriod,
  clearPeriod,
  chargeType,
  setChargeType,
  search,
  applySearch,
  summary,
}: TransactionsTableHeaderProps) => {
  const [searchInput, setSearchInput] = useState<string>(search?.value ?? "");
  const isCdrLookup = search?.kind === "cdr";

  // Follow the applied search (e.g. "Search as User ID instead" keeps the value).
  useEffect(() => {
    if (search) setSearchInput(search.value);
  }, [search]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    applySearch(searchInput);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    // Emptying the box returns to the normal list straight away.
    if (value.trim() === "" && search) applySearch("");
  };

  return (
    <div className="mb-4">
      <style>{HEADER_STYLE}</style>

      <div className="mb-3">
        <h2 className="text-2xl font-bold text-900 m-0">Payments</h2>
        <div className="text-sm text-500 mt-1" data-testid="payments-summary">
          {summary}
        </div>
      </div>

      <div className="flex flex-column gap-3 lg:flex-row lg:align-items-center lg:justify-content-between">
        <div className="flex align-items-center gap-2 flex-shrink-0">
          <span className="text-sm font-medium text-600">Type</span>
          <SelectButton
            className="segmented-select charge-type-select"
            value={chargeType}
            onChange={(e) => e.value !== null && setChargeType(e.value)}
            options={CHARGE_TYPE_OPTIONS}
            allowEmpty={false}
            disabled={isCdrLookup}
            aria-label="Payment type"
          />
        </div>

        <div className="flex flex-column gap-2 sm:flex-row sm:align-items-center">
          <form className="flex align-items-center gap-2" role="search" onSubmit={handleSubmit}>
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={searchInput}
                onChange={(e) => handleSearchChange(e?.target?.value ?? "")}
                placeholder="Search by CDR ID or User ID…"
                aria-label="Search by CDR ID or User ID"
                className="w-full sm:w-24rem"
              />
            </IconField>
            <Button
              type="submit"
              icon="pi pi-search"
              rounded
              severity="success"
              tooltip="Search all payments"
              tooltipOptions={{ position: "top" }}
              aria-label="Search"
            />
          </form>

          <TransactionsPeriodSelector
            appliedRange={appliedRange}
            applyPeriod={applyPeriod}
            clearPeriod={clearPeriod}
            disabled={isCdrLookup}
          />
        </div>
      </div>

      {isCdrLookup && (
        <div className="text-sm text-500 mt-2" data-testid="cdr-lookup-hint">
          <i className="pi pi-info-circle mr-2" />
          Looking up a single CDR — date range and type don't apply. Clear the search to
          return to the list.
        </div>
      )}
    </div>
  );
};

const InvoiceColumnBody = (rowData: Transaction) => {
  const hasUrl = rowData?.invoiceUrl != null && rowData?.invoiceUrl?.trim() !== "";
  return (
    <Button
      icon="pi pi-file-pdf"
      rounded
      text
      severity={hasUrl ? "info" : "secondary"}
      disabled={!hasUrl}
      onClick={(e) => {
        e.stopPropagation();
        if (hasUrl) {
          window.open(rowData.invoiceUrl as string, "_blank");
        }
      }}
      tooltip={hasUrl ? "Open invoice" : "No invoice available"}
      tooltipOptions={{ position: "top" }}
      style={{
        opacity: hasUrl ? 1 : 0.35,
        cursor: hasUrl ? "pointer" : "default",
      }}
    />
  );
};

type TagSeverity = "info" | "success" | "warning" | "danger" | null | undefined;

type StatusMeta = {
  label?: string;
  severity?: TagSeverity;
  className?: string;
  icon?: string;
};

// Keyed by the normalised status (upper-case, no spaces/underscores/dashes) because the
// payment services disagree on casing: "paid" vs "PAID", "FreeCharge" vs "Free Charge".
const STATUS_META: Record<string, StatusMeta> = {
  PAID: { severity: "success" },
  SUCCEEDED: { severity: "success" },
  COMPLETED: { severity: "success" },
  PROCESSING: { severity: "info" },
  PENDING: { severity: "info" },
  OPEN: { severity: "info" },
  REFUNDED: { severity: "warning" },
  FAILED: { severity: "danger" },
  WALLETCHARGED: {
    label: "WALLET CHARGED",
    severity: undefined,
    className: "bg-purple-100 text-purple-800",
    icon: "pi pi-wallet",
  },
  FREECHARGE: {
    label: "FREE CHARGE",
    severity: undefined,
    className: "bg-teal-100 text-teal-800",
  },
};

const normaliseStatus = (status: string | null | undefined): string =>
  (status ?? "").toUpperCase().replace(/[\s_-]/g, "");

const PaymentStatusBody = (rowData: Transaction) => {
  const status = rowData.paymentStatus;
  const meta = STATUS_META[normaliseStatus(status)];
  const label = meta?.label ?? (status ? status.toUpperCase() : "UNKNOWN");
  const className = meta
    ? `font-medium ${meta.className ?? ""}`.trim()
    : "font-medium text-500 surface-200";

  return (
    <Tag
      value={label}
      severity={meta?.severity}
      icon={meta?.icon}
      className={className}
      data-testid="payment-status-tag"
      style={{ letterSpacing: "0.4px", fontSize: "0.72rem", borderRadius: "999px" }}
    />
  );
};

/** Gross session cost: the wallet split's session total, else what was charged (pre-wallet rows). */
const sessionCost = (rowData: Transaction): number | null => {
  const value = rowData.sessionTotalAmount ?? rowData.totalAmount;
  return value === null || value === undefined ? null : Number(value);
};

const formatAmount = (value: number | null | undefined) => `€${Number(value ?? 0).toFixed(2)}`;

// Amount = session cost (credit + card). When wallet credit funded part of the session, a
// secondary line shows the split; card-only and pre-wallet rows render as before.
const AmountBody = (rowData: Transaction) => {
  const total = sessionCost(rowData);
  if (total === null) return <span className="text-400">—</span>;

  const credit = Number(rowData.walletCreditAmount ?? 0);
  const card = rowData.cardAmount ?? rowData.totalAmount;

  return (
    <div className="flex flex-column align-items-end">
      <span className="font-semibold text-900">{formatAmount(total)}</span>
      {credit > 0 && (
        <span className="text-xs text-500 white-space-nowrap">
          Credit {formatAmount(credit)} · Card {formatAmount(card)}
        </span>
      )}
    </div>
  );
};

const CreatedAtBody = (rowData: Transaction) => (
  <span className="text-600 font-medium">{formatDate(rowData.createdAt)}</span>
);

const CdrIdBody = (rowData: Transaction) => (
  <span className="font-mono text-sm text-700">{rowData.cdrId}</span>
);

const UserIdBody = (rowData: Transaction) => (
  <span className="font-mono text-sm text-700">{rowData.userId}</span>
);

const StationIdBody = (rowData: Transaction) => (
  <span className="font-mono text-sm text-700">{rowData.stationId}</span>
);

interface TransactionsTableProps {
  items: Transaction[];
  loading: boolean;
  error: boolean;
  chargeType: ChargeType;
  dateRange: DateRange;
  order: TransactionOrder;
  search: TransactionSearch;
  pageNumber: number;
  pageSize: number;
  hasPrev: boolean;
  hasNext: boolean;
  total: number | null;
  totalLoading: boolean;
  goNext: () => void;
  goPrev: () => void;
  retry: () => void;
  applyPeriod: (period: DateRange) => void;
  clearPeriod: () => void;
  setChargeType: (value: ChargeType) => void;
  setOrder: (value: TransactionOrder) => void;
  applySearch: (value: string, kind?: TransactionSearchKind) => void;
}

export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  items,
  loading,
  error,
  chargeType,
  dateRange,
  order,
  search,
  pageNumber,
  pageSize,
  hasPrev,
  hasNext,
  total,
  totalLoading,
  goNext,
  goPrev,
  retry,
  applyPeriod,
  clearPeriod,
  setChargeType,
  setOrder,
  applySearch,
}) => {
  const isCdrLookup = search?.kind === "cdr";

  const summary = formatPaymentsSummary({
    pageNumber,
    pageSize,
    total,
    totalLoading,
    isCdrLookup,
    rowCount: loading || error ? null : items?.length ?? 0,
  });

  // While loading every cell is a Skeleton; otherwise the column's own body.
  const cell =
    (body: (rowData: Transaction) => React.ReactNode) => (rowData: Transaction) =>
      loading ? <Skeleton height="1.25rem" data-testid="transactions-skeleton" /> : body(rowData);

  const rows = loading ? SKELETON_ROWS : error ? [] : items ?? [];

  const handleSort = (e: DataTableSortEvent) => {
    setOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const errorState = (
    <Message
      severity="error"
      className="w-full justify-content-between"
      content={
        <div className="flex align-items-center justify-content-between w-full gap-3">
          <span className="text-sm">Couldn't load payments. Please try again.</span>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            size="small"
            severity="danger"
            text
            onClick={retry}
          />
        </div>
      }
    />
  );

  const emptyState = search ? (
    <div className="flex flex-column align-items-center justify-content-center py-6 gap-3">
      <i className="pi pi-search text-6xl text-300" />
      <span className="text-lg text-500">
        No payment found for {SEARCH_KIND_LABEL[search.kind]}{" "}
        <span className="font-mono">{search.value}</span>
      </span>
      <Button
        label={`Search as ${SEARCH_KIND_LABEL[otherSearchKind(search.kind)]} instead`}
        text
        severity="secondary"
        onClick={() => applySearch(search.value, otherSearchKind(search.kind))}
      />
    </div>
  ) : (
    <div className="flex flex-column align-items-center justify-content-center py-6 gap-3">
      <i className="pi pi-inbox text-6xl text-300" />
      <span className="text-lg text-500">No payments found</span>
      <span className="text-sm text-400">Try adjusting the date range or type</span>
    </div>
  );

  const paginationFooter = (
    <div className="flex align-items-center justify-content-between pt-4">
      <Button
        label="Previous"
        icon="pi pi-chevron-left"
        outlined
        severity="secondary"
        disabled={!hasPrev || loading}
        onClick={goPrev}
      />
      <span className="text-sm text-500">Page {pageNumber}</span>
      <Button
        label="Next"
        icon="pi pi-chevron-right"
        iconPos="right"
        outlined
        severity="secondary"
        disabled={!hasNext || loading || error}
        onClick={goNext}
      />
    </div>
  );

  return (
    <Card className="shadow-2 border-round">
      <TransactionsTableHeader
        appliedRange={dateRange}
        applyPeriod={applyPeriod}
        clearPeriod={clearPeriod}
        chargeType={chargeType}
        setChargeType={setChargeType}
        search={search}
        applySearch={applySearch}
        summary={summary}
      />

      {/*
        Lazy: pages come from the server one cursor at a time (Previous/Next in the
        footer), and search and sort are done by the server too (CLR-278), so the
        table must not filter or sort the rows it was given.
      */}
      <DataTable
        value={rows}
        lazy
        dataKey="cdrId"
        emptyMessage={error ? errorState : emptyState}
        className="p-datatable-sm"
        sortMode="single"
        sortField="createdAt"
        sortOrder={order === "asc" ? 1 : -1}
        onSort={handleSort}
        stripedRows
        rowHover={!loading}
        footer={paginationFooter}
      >
        <Column
          field="cdrId"
          header="CDR ID"
          body={cell(CdrIdBody)}
          style={{ minWidth: "180px" }}
        />
        <Column
          field="userId"
          header="User ID"
          body={cell(UserIdBody)}
          style={{ minWidth: "160px" }}
        />
        <Column
          field="stationId"
          header="Station ID"
          body={cell(StationIdBody)}
          style={{ minWidth: "140px" }}
        />
        <Column
          header="Status"
          body={cell(PaymentStatusBody)}
          style={{ width: "140px", textAlign: "center" }}
        />
        <Column
          header="Amount"
          body={cell(AmountBody)}
          style={{ width: "170px", textAlign: "right" }}
        />
        <Column
          header="Invoice"
          body={cell(InvoiceColumnBody)}
          style={{ width: "90px", textAlign: "center" }}
        />
        <Column
          field="createdAt"
          header="Created At"
          body={cell(CreatedAtBody)}
          sortable
          sortField="createdAt"
          style={{ minWidth: "160px" }}
        />
      </DataTable>
    </Card>
  );
};
